import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import { useLanguage } from '../context/LanguageContext';
import { useGeoCountry } from '../hooks/useGeoCountry';
import { countryToCode } from '../data/euCountries';
import CountrySelect from '../components/CountrySelect';
import './Checkout.css';

/** PVM tarifas pagal pristatymo šalį: Vokietija 19%, kiti 21%. */
function getTaxRateForCountry(country) {
  if (!country || typeof country !== 'string') return 0.21;
  const c = country.trim().toUpperCase();
  if (c === 'DE' || c === 'DEUTSCHLAND' || c === 'GERMANY' || c === 'VOKIETIJA') return 0.19;
  return 0.21;
}

/** Pilnas paveikslėlio URL mokėjimo priemonėms (relatyvus /uploads/... veikia bet kurioje aplinkoje). */
function getPaymentImageSrc(imageUrl) {
  if (!imageUrl || typeof imageUrl !== 'string') return '';
  const trimmed = imageUrl.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  const base = typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.REACT_APP_API_URL ? new URL(process.env.REACT_APP_API_URL).origin : '');
  return base + (trimmed.startsWith('/') ? trimmed : '/' + trimmed);
}

function Checkout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { language, t } = useLanguage();
  const [paymentMethod, setPaymentMethod] = useState('');
  const [stripePaymentOption, setStripePaymentOption] = useState('card');
  const [shippingMethod, setShippingMethod] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    first_name: '',
    last_name: '',
    company: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    country: 'LT',
    phone: '',
    email: '',
  });
  const [shippingAddress, setShippingAddress] = useState({
    first_name: '',
    last_name: '',
    company: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    country: 'LT',
    phone: '',
    email: '',
  });
  const { countryCode: geoCountryCode } = useGeoCountry(language);

  useEffect(() => {
    if (!geoCountryCode) return;
    setBillingAddress((prev) => {
      const current = countryToCode(prev.country);
      if (current !== 'LT') return prev;
      return { ...prev, country: geoCountryCode };
    });
    setShippingAddress((prev) => {
      const current = countryToCode(prev.country);
      if (current !== 'LT') return prev;
      return { ...prev, country: geoCountryCode };
    });
  }, [geoCountryCode]);

  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState(null);
  const [selectedShippingAddressId, setSelectedShippingAddressId] = useState(null);
  const [saveBillingAddress, setSaveBillingAddress] = useState(false);
  const [saveShippingAddress, setSaveShippingAddress] = useState(false);
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [useStoreCredits, setUseStoreCredits] = useState(false);
  const [storeCreditsToUse, setStoreCreditsToUse] = useState(0);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const { data: cart } = useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const sessionId = localStorage.getItem('sessionId');
      const res = await apiClient.get('/cart', {
        headers: sessionId ? { 'x-session-id': sessionId } : {},
      });
      return res.data;
    },
  });

  // Didžiausias siuntos dydis iš krepšelio prekių (xs < s < m < l < xl) – pristatymo kaina filtruojama pagal šalį, tada siuntos dydį, tada bazinę kainą
  const packageSizeOrder = { xs: 0, s: 1, m: 2, l: 3, xl: 4 };
  const cartPackageSize = (() => {
    if (!cart?.items?.length) return null;
    let maxOrder = -1;
    let found = null;
    for (const item of cart.items) {
      const ps = (item.package_size || '').toString().toLowerCase().trim();
      if (ps && packageSizeOrder[ps] !== undefined && packageSizeOrder[ps] > maxOrder) {
        maxOrder = packageSizeOrder[ps];
        found = ps;
      }
    }
    return found;
  })();

  const { data: shippingMethods } = useQuery({
    queryKey: ['shipping-methods', cart?.total, billingAddress.country, billingAddress.city, billingAddress.postal_code, cartPackageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (cart?.total != null) params.append('order_value', String(cart.total));
      const totalWeight = cart?.items?.reduce((sum, item) => sum + (item.weight || 0) * item.quantity, 0) ?? 0;
      params.append('weight', String(totalWeight >= 0 ? totalWeight : 0));
      params.append('country', (billingAddress.country || 'LT').trim() || 'LT');
      if (billingAddress.city) params.append('city', billingAddress.city);
      if (billingAddress.postal_code) params.append('postal_code', billingAddress.postal_code);
      if (cartPackageSize) params.append('package_size', cartPackageSize);
      
      const res = await apiClient.get(`/shipping?${params.toString()}`);
      return res.data;
    },
    enabled: !!cart && cart.items.length > 0,
  });

  const { data: paymentMethods } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: async () => {
      try {
        const res = await apiClient.get('/payment-methods');
        return res.data || [];
      } catch (error) {
        return [];
      }
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Gauti išsaugotus adresus (tik jei vartotojas prisijungęs)
  const token = localStorage.getItem('token');
  const { data: savedAddresses } = useQuery({
    queryKey: ['user-addresses'],
    queryFn: async () => {
      const res = await apiClient.get('/user/addresses');
      return res.data;
    },
    enabled: !!token, // Tik jei vartotojas prisijungęs
  });

  // Gauti vartotojo profilio duomenis (vardas, pavardė, telefonas, el. paštas)
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const res = await apiClient.get('/user/profile');
      return res.data;
    },
    enabled: !!token, // Tik jei vartotojas prisijungęs
  });

  // Gauti vartotojo parduotuvės kreditų balansą
  const { data: storeCreditsData } = useQuery({
    queryKey: ['store-credits'],
    queryFn: async () => {
      const res = await apiClient.get('/store-credits/my-credits');
      return res.data;
    },
    enabled: !!token, // Tik jei vartotojas prisijungęs
  });

  const availableCredits = storeCreditsData?.total_balance || 0;

  // Nustatyti numatytąjį adresą, jei yra (tik vieną kartą)
  useEffect(() => {
    if (savedAddresses && savedAddresses.length > 0 && !useCustomAddress && !selectedBillingAddressId) {
      const defaultBilling = savedAddresses.find(a => a.is_default && (a.address_type === 'billing' || a.address_type === 'both'));
      const defaultShipping = savedAddresses.find(a => a.is_default && (a.address_type === 'shipping' || a.address_type === 'both'));
      
      if (defaultBilling) {
        setSelectedBillingAddressId(defaultBilling.id);
        setBillingAddress({
          first_name: defaultBilling.first_name,
          last_name: defaultBilling.last_name,
          company: defaultBilling.company || '',
          address_line1: defaultBilling.address_line1,
          address_line2: defaultBilling.address_line2 || '',
          city: defaultBilling.city,
          postal_code: defaultBilling.postal_code,
          country: defaultBilling.country,
          phone: defaultBilling.phone || '',
          email: defaultBilling.email || '',
        });
      }
      
      if (defaultShipping) {
        setSelectedShippingAddressId(defaultShipping.id);
        setShippingAddress({
          first_name: defaultShipping.first_name,
          last_name: defaultShipping.last_name,
          company: defaultShipping.company || '',
          address_line1: defaultShipping.address_line1,
          address_line2: defaultShipping.address_line2 || '',
          city: defaultShipping.city,
          postal_code: defaultShipping.postal_code,
          country: defaultShipping.country,
          phone: defaultShipping.phone || '',
          email: defaultShipping.email || '',
        });
      }
      
      if (defaultBilling || defaultShipping) {
        setUseCustomAddress(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddresses]);

  // Jei nėra išsaugotų adresų, užpildyti formą su profilio duomenimis
  useEffect(() => {
    if (token && userProfile && (!savedAddresses || savedAddresses.length === 0)) {
      // Užpildyti billing adresą su profilio duomenimis (tik jei laukai tušti)
      if (!billingAddress.first_name && userProfile.first_name) {
        setBillingAddress(prev => ({
          ...prev,
          first_name: userProfile.first_name || '',
          last_name: userProfile.last_name || '',
          phone: userProfile.phone || '',
          email: userProfile.email || '',
        }));
      }
      
      // Užpildyti shipping adresą su profilio duomenimis (tik jei laukai tušti)
      if (!shippingAddress.first_name && userProfile.first_name) {
        setShippingAddress(prev => ({
          ...prev,
          first_name: userProfile.first_name || '',
          last_name: userProfile.last_name || '',
          phone: userProfile.phone || '',
          email: userProfile.email || '',
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userProfile, savedAddresses]);

  // Atnaujinti adresą, kai pasirenkamas iš sąrašo
  useEffect(() => {
    if (selectedBillingAddressId && savedAddresses) {
      const address = savedAddresses.find(a => a.id === parseInt(selectedBillingAddressId));
      if (address) {
        setBillingAddress({
          first_name: address.first_name,
          last_name: address.last_name,
          company: address.company || '',
          address_line1: address.address_line1,
          address_line2: address.address_line2 || '',
          city: address.city,
          postal_code: address.postal_code,
          country: address.country,
          phone: address.phone || '',
          email: address.email || '',
        });
      }
    }
  }, [selectedBillingAddressId, savedAddresses]);

  useEffect(() => {
    if (selectedShippingAddressId && savedAddresses) {
      const address = savedAddresses.find(a => a.id === parseInt(selectedShippingAddressId));
      if (address) {
        setShippingAddress({
          first_name: address.first_name,
          last_name: address.last_name,
          company: address.company || '',
          address_line1: address.address_line1,
          address_line2: address.address_line2 || '',
          city: address.city,
          postal_code: address.postal_code,
          country: address.country,
          phone: address.phone || '',
          email: address.email || '',
        });
      }
    }
  }, [selectedShippingAddressId, savedAddresses]);

  // Stripe: užsakymas kuriamas tik po apmokėjimo – tik payment/create, be POST /orders
  const createStripeCheckoutMutation = useMutation({
    mutationFn: async (orderData) => {
      const sessionId = localStorage.getItem('sessionId');
      const token = localStorage.getItem('token');
      const headers = {};
      if (sessionId) headers['x-session-id'] = sessionId;
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const body = {
        payment_method: 'stripe',
        billing_address: orderData.billing_address,
        shipping_address: orderData.shipping_address,
        shipping_method_id: orderData.shipping_method_id,
        store_credits_used: orderData.store_credits_used || 0,
        language: orderData.language || 'lt',
      };
      if (orderData.stripe_payment_option) {
        body.stripe_payment_option = orderData.stripe_payment_option;
      }
      const res = await apiClient.post('/payment/create', body, { headers });
      return res.data;
    },
    onSuccess: (data) => {
      if (data?.session?.url) {
        window.location.href = data.session.url;
      } else {
        notification.error(t('error'));
      }
    },
    onError: (error) => {
      notification.info(error.response?.data?.error || t('error'));
    },
  });

  const createOrderMutation = useMutation({
    mutationFn: async (orderData) => {
      const sessionId = localStorage.getItem('sessionId');
      const token = localStorage.getItem('token');
      const headers = {};
      if (sessionId) headers['x-session-id'] = sessionId;
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const orderResponse = await apiClient.post('/orders', orderData, { headers });
      const orderId = orderResponse?.data?.order?.id;
      
      if (!orderId) {
        throw new Error('Nepavyko sukurti užsakymo');
      }
      
      // PayPal, Banklink – užsakymas jau sukurtas, reikia tik mokėjimo sesijos
      if (['paypal', 'banklink'].includes(orderData.payment_method)) {
        try {
          const paymentResponse = await apiClient.post('/payment/create', {
            order_id: orderId,
            payment_method: orderData.payment_method
          }, { headers });
          
          return {
            order: orderResponse.data.order,
            payment: paymentResponse.data,
            payment_method: orderData.payment_method
          };
        } catch (paymentError) {
          return {
            order: orderResponse.data.order,
            payment: null,
            payment_method: orderData.payment_method,
            error: paymentError.response?.data?.error || 'Nepavyko sukurti mokėjimo sesijos'
          };
        }
      }
      
      return {
        order: orderResponse.data.order,
        payment: null,
        payment_method: orderData.payment_method
      };
    },
    onSuccess: (response) => {
      const orderId = response?.order?.id;
      
      if (!orderId) {
        notification.error(t('error'));
        return;
      }
      
      if (response?.payment?.session) {
        if (response.payment_method === 'paypal' && response.payment.session.approval_url) {
          window.location.href = response.payment.session.approval_url;
        } else if (response.payment_method === 'banklink' && response.payment.session.redirect_url) {
          window.location.href = response.payment.session.redirect_url;
        } else {
          queryClient.invalidateQueries({ queryKey: ['cart'] });
          navigate(`/orders/${orderId}`);
        }
      } else if (response?.error) {
        notification.warning(`Užsakymas sukurtas, bet mokėjimo sesijos sukurti nepavyko: ${response.error}`);
        queryClient.invalidateQueries({ queryKey: ['cart'] });
        localStorage.removeItem('sessionId');
        navigate(`/orders/${orderId}`);
      } else {
        queryClient.invalidateQueries({ queryKey: ['cart'] });
        localStorage.removeItem('sessionId');
        navigate(`/orders/${orderId}`);
      }
    },
    onError: (error) => {
      notification.info(error.response?.data?.error || t('error'));
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!shippingMethod) {
      notification.warning(t('chooseShipping'));
      return;
    }

    if (!paymentMethod) {
      notification.warning(t('choosePayment'));
      return;
    }

    if (!agreeToTerms) {
      notification.warning('Pateikdami užsakymą turite sutikti su naudojimosi sąlygomis ir prekių pristatymo bei grąžinimo sąlygomis.');
      return;
    }

    const finalShippingAddress = sameAsBilling ? billingAddress : shippingAddress;
    const billingCountry = countryToCode(billingAddress.country) || 'LT';
    const shippingCountry = countryToCode(finalShippingAddress.country) || 'LT';

    const orderData = {
      billing_address: { ...billingAddress, country: billingCountry },
      shipping_address: { ...finalShippingAddress, country: shippingCountry },
      payment_method: paymentMethod,
      shipping_method_id: shippingMethod,
      store_credits_used: useStoreCredits && storeCreditsToUse > 0 ? parseFloat(storeCreditsToUse) : 0,
      language: language || 'lt',
    };

    // Jei vartotojas prisijungęs ir pažymėjo checkbox'us, išsaugoti adresus
    if (token) {
      if (saveBillingAddress && !selectedBillingAddressId) {
        apiClient.post('/user/addresses', {
          address_type: 'billing',
          is_default: false,
          ...billingAddress,
          country: billingCountry
        }).catch(() => {});
      }
      
      if (saveShippingAddress && !sameAsBilling && !selectedShippingAddressId) {
        apiClient.post('/user/addresses', {
          address_type: 'shipping',
          is_default: false,
          ...finalShippingAddress,
          country: shippingCountry
        }).catch(() => {});
      }
    }

    // Stripe: užsakymas kuriamas tik po apmokėjimo – tik payment/create
    const isStripeFlow = paymentMethod === 'stripe' || paymentMethod === 'stripe_card' || paymentMethod === 'stripe_apple_pay' || paymentMethod === 'stripe_google_pay';
    if (isStripeFlow) {
      const payload = { ...orderData };
      if (paymentMethod === 'stripe_card') {
        payload.stripe_payment_option = 'card';
      } else if (paymentMethod === 'stripe_apple_pay') {
        payload.stripe_payment_option = 'apple_pay';
      } else if (paymentMethod === 'stripe_google_pay') {
        payload.stripe_payment_option = 'google_pay';
      } else {
        const stripeMethod = paymentMethods?.find(m => (m.method_key || '').toLowerCase() === 'stripe');
        const allowedTypes = Array.isArray(stripeMethod?.stripe_payment_types) ? stripeMethod.stripe_payment_types : [];
        if (allowedTypes.length > 0 && allowedTypes.includes(stripePaymentOption)) {
          payload.stripe_payment_option = stripePaymentOption;
        }
      }
      createStripeCheckoutMutation.mutate(payload);
      return;
    }
    createOrderMutation.mutate(orderData);
  };

  // Kai pasirenkamas Stripe ir API grąžino stripe_payment_types – numatyti pirmą tipą iš sąrašo
  React.useEffect(() => {
    if (paymentMethod !== 'stripe' || !paymentMethods || !Array.isArray(paymentMethods)) return;
    const stripeMethod = paymentMethods.find(m => (m.method_key || '').toLowerCase() === 'stripe');
    const types = Array.isArray(stripeMethod?.stripe_payment_types) ? stripeMethod.stripe_payment_types : [];
    if (types.length > 0 && !types.includes(stripePaymentOption)) {
      setStripePaymentOption(types[0]);
    }
  }, [paymentMethod, paymentMethods, stripePaymentOption]);

  // Nustatyti pirmąjį aktyvų mokėjimo būdą kaip numatytąjį
  React.useEffect(() => {
    if (paymentMethods && Array.isArray(paymentMethods) && paymentMethods.length > 0 && !paymentMethod) {
      const enabledMethods = paymentMethods.filter(m => {
        const isEnabled = m.is_enabled === true || 
                         m.is_enabled === 1 || 
                         m.is_enabled === '1' ||
                         String(m.is_enabled).toLowerCase() === 'true';
        
        const isActive = m.active === true ||
                        m.active === 1 ||
                        m.active === '1' ||
                        String(m.active).toLowerCase() === 'true';
        
        const statusActive = m.status === 'active' ||
                            m.status === 'enabled' ||
                            m.status === 1;
        
        const notDisabled = m.is_enabled !== false &&
                           m.is_enabled !== 0 &&
                           m.is_enabled !== '0' &&
                           m.active !== false &&
                           m.active !== 0 &&
                           m.active !== '0' &&
                           m.status !== 'disabled' &&
                           m.status !== 'inactive';
        
        return isEnabled || isActive || statusActive || (notDisabled && m.is_enabled !== null && m.active !== null);
      });
      
      if (enabledMethods.length > 0) {
        setPaymentMethod(enabledMethods[0].method_key);
      }
    }
  }, [paymentMethods, paymentMethod]);

  // Pristatymo kaina: naudoti calculated_cost (iš taisyklių), jei yra, kitaip base_cost/cost
  const getShippingCost = (m) => parseFloat(m.calculated_cost ?? m.cost ?? m.base_cost) ?? 0;

  // Pristatymo parinkimas pagal taisykles: jei pasiekta nemokamo riba – nemokamas; kitaip – mažiausia kaina
  useEffect(() => {
    if (!shippingMethods?.length || !cart?.total) return;
    const cartTotal = parseFloat(cart.total) || 0;
    const freeMethod = shippingMethods.find(
      (m) => getShippingCost(m) < 0.01 || (m.free_shipping_threshold != null && cartTotal >= parseFloat(m.free_shipping_threshold))
    );
    if (freeMethod) {
      setShippingMethod(String(freeMethod.id));
    } else {
      const cheapest = shippingMethods.reduce((best, m) => {
        const cost = getShippingCost(m);
        const bestCost = best ? getShippingCost(best) : Infinity;
        return cost < bestCost ? m : best;
      }, null);
      if (cheapest) setShippingMethod(String(cheapest.id));
    }
  }, [shippingMethods, cart?.total]);

  if (!cart || cart.items.length === 0) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="checkout-header">
            <h1>{t('checkoutTitle')}</h1>
          </div>
          <div className="error">{t('cartEmpty')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-header">
          <h1>{t('checkoutTitle')}</h1>
          <p className="checkout-subtitle">{t('checkoutSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="checkout-form">
        <div className="form-section">
          <h2>{t('billingAddress')}</h2>
          
          {token && savedAddresses && savedAddresses.length > 0 && (
            <div className="address-selection">
              <label>
                <input
                  type="radio"
                  name="billing-address-type"
                  checked={!useCustomAddress}
                  onChange={() => setUseCustomAddress(false)}
                />
                {t('selectSavedAddresses')}
              </label>
              <label>
                <input
                  type="radio"
                  name="billing-address-type"
                  checked={useCustomAddress}
                  onChange={() => {
                    setUseCustomAddress(true);
                    setSelectedBillingAddressId(null);
                  }}
                />
                {t('enterNewAddress')}
              </label>
            </div>
          )}

          {token && savedAddresses && savedAddresses.length > 0 && !useCustomAddress && (
            <div className="form-group">
              <label>{t('selectBillingAddress')}</label>
              <select
                value={selectedBillingAddressId || ''}
                onChange={(e) => setSelectedBillingAddressId(e.target.value ? parseInt(e.target.value) : null)}
                className="input"
              >
                <option value="">{t('selectAddress')}</option>
                {savedAddresses
                  .filter(a => a.address_type === 'billing' || a.address_type === 'both')
                  .map((address) => (
                    <option key={address.id} value={address.id}>
                      {address.first_name} {address.last_name} - {address.address_line1}, {address.city}
                      {address.is_default ? ` (${t('defaultAddress')})` : ''}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {(useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0) && (
            <>
              <div className="form-grid">
                <input
                  type="text"
                  placeholder={`${t('firstName')} *`}
                  value={billingAddress.first_name}
                  onChange={(e) => setBillingAddress({ ...billingAddress, first_name: e.target.value })}
                  required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                  disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedBillingAddressId}
                  className="input"
                />
                <input
                  type="text"
                  placeholder={`${t('lastName')} *`}
                  value={billingAddress.last_name}
                  onChange={(e) => setBillingAddress({ ...billingAddress, last_name: e.target.value })}
                  required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                  disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedBillingAddressId}
                  className="input"
                />
                <input
                  type="text"
                  placeholder={t('company')}
                  value={billingAddress.company}
                  onChange={(e) => setBillingAddress({ ...billingAddress, company: e.target.value })}
                  disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedBillingAddressId}
                  className="input full-width"
                />
                <input
                  type="text"
                  placeholder={`${t('address')} *`}
                  value={billingAddress.address_line1}
                  onChange={(e) => setBillingAddress({ ...billingAddress, address_line1: e.target.value })}
                  required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                  disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedBillingAddressId}
                  className="input full-width"
                />
                <input
                  type="text"
                  placeholder={t('addressLine2')}
                  value={billingAddress.address_line2}
                  onChange={(e) => setBillingAddress({ ...billingAddress, address_line2: e.target.value })}
                  disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedBillingAddressId}
                  className="input full-width"
                />
                <input
                  type="text"
                  placeholder={`${t('city')} *`}
                  value={billingAddress.city}
                  onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                  required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                  disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedBillingAddressId}
                  className="input"
                />
                <input
                  type="text"
                  placeholder={`${t('postalCode')} *`}
                  value={billingAddress.postal_code}
                  onChange={(e) => setBillingAddress({ ...billingAddress, postal_code: e.target.value })}
                  required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                  disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedBillingAddressId}
                  className="input"
                />
                <div className="form-group full-width">
                  <label>{t('country')} *</label>
                  <CountrySelect
                    value={billingAddress.country}
                    onChange={(code) => setBillingAddress({ ...billingAddress, country: code })}
                    language={language}
                    disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedBillingAddressId}
                    className="input"
                    required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                  />
                </div>
                <input
                  type="tel"
                  placeholder={`${t('phone')} *`}
                  value={billingAddress.phone}
                  onChange={(e) => setBillingAddress({ ...billingAddress, phone: e.target.value })}
                  required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                  disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedBillingAddressId}
                  className="input"
                />
                <input
                  type="email"
                  placeholder={t('email')}
                  value={billingAddress.email}
                  onChange={(e) => setBillingAddress({ ...billingAddress, email: e.target.value })}
                  disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedBillingAddressId}
                  className="input full-width"
                />
              </div>
              
              {/* Checkbox išsaugoti adresą */}
              {token && useCustomAddress && (
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={saveBillingAddress}
                      onChange={(e) => setSaveBillingAddress(e.target.checked)}
                    />
                    {t('saveThisAddress')}
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        <div className="form-section">
          <label>
            <input
              type="checkbox"
              checked={sameAsBilling}
              onChange={(e) => {
                setSameAsBilling(e.target.checked);
                if (e.target.checked) {
                  setSelectedShippingAddressId(selectedBillingAddressId);
                  setShippingAddress(billingAddress);
                } else {
                  setSelectedShippingAddressId(null);
                }
              }}
            />
            {t('shippingSameAsBilling')}
          </label>

          {!sameAsBilling && (
            <>
              {/* Pasirinkti iš sąrašo arba įvesti ranka */}
              {token && savedAddresses && savedAddresses.length > 0 && (
                <div className="address-selection">
                  <label>
                    <input
                      type="radio"
                      name="shipping-address-type"
                      checked={!useCustomAddress}
                      onChange={() => setUseCustomAddress(false)}
                    />
                    {t('selectSavedAddresses')}
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="shipping-address-type"
                      checked={useCustomAddress}
                      onChange={() => {
                        setUseCustomAddress(true);
                        setSelectedShippingAddressId(null);
                      }}
                    />
                    {t('enterNewAddress')}
                  </label>
                </div>
              )}

              {token && savedAddresses && savedAddresses.length > 0 && !useCustomAddress && (
                <div className="form-group">
                  <label>{t('selectShippingAddress')}</label>
                  <select
                    value={selectedShippingAddressId || ''}
                    onChange={(e) => setSelectedShippingAddressId(e.target.value ? parseInt(e.target.value) : null)}
                    className="input"
                  >
                    <option value="">{t('selectAddress')}</option>
                    {savedAddresses
                      .filter(a => a.address_type === 'shipping' || a.address_type === 'both')
                      .map((address) => (
                        <option key={address.id} value={address.id}>
                          {address.first_name} {address.last_name} - {address.address_line1}, {address.city}
                          {address.is_default ? ` (${t('defaultAddress')})` : ''}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {(useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0) && (
                <>
                  <div className="form-grid">
                    <input
                      type="text"
                      placeholder={`${t('firstName')} *`}
                      value={shippingAddress.first_name}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, first_name: e.target.value })}
                      required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                      disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedShippingAddressId}
                      className="input"
                    />
                    <input
                      type="text"
                      placeholder={`${t('lastName')} *`}
                      value={shippingAddress.last_name}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, last_name: e.target.value })}
                      required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                      disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedShippingAddressId}
                      className="input"
                    />
                    <input
                      type="text"
                      placeholder={`${t('address')} *`}
                      value={shippingAddress.address_line1}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, address_line1: e.target.value })}
                      required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                      disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedShippingAddressId}
                      className="input full-width"
                    />
                    <input
                      type="text"
                      placeholder={`${t('city')} *`}
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                      required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                      disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedShippingAddressId}
                      className="input"
                    />
                    <input
                      type="text"
                      placeholder={`${t('postalCode')} *`}
                      value={shippingAddress.postal_code}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, postal_code: e.target.value })}
                      required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                      disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedShippingAddressId}
                      className="input"
                    />
                    <div className="form-group full-width">
                      <label>{t('country')} *</label>
                      <CountrySelect
                        value={shippingAddress.country}
                        onChange={(code) => setShippingAddress({ ...shippingAddress, country: code })}
                        language={language}
                        disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedShippingAddressId}
                        className="input"
                        required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                      />
                    </div>
                    <input
                      type="tel"
                      placeholder={`${t('phone')} *`}
                      value={shippingAddress.phone}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                      required={useCustomAddress || !token || !savedAddresses || savedAddresses.length === 0}
                      disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedShippingAddressId}
                      className="input"
                    />
                    <input
                      type="email"
                      placeholder={t('email')}
                      value={shippingAddress.email}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, email: e.target.value })}
                      disabled={!useCustomAddress && token && savedAddresses && savedAddresses.length > 0 && selectedShippingAddressId}
                      className="input full-width"
                    />
                  </div>
                  
                  {/* Checkbox išsaugoti adresą */}
                  {token && useCustomAddress && (
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={saveShippingAddress}
                          onChange={(e) => setSaveShippingAddress(e.target.checked)}
                        />
                        {t('saveThisAddress')}
                      </label>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="form-section">
          <h2>{t('deliveryMethod')}</h2>
          {shippingMethods && shippingMethods.length > 0 ? (
            (() => {
              const cartTotal = parseFloat(cart?.total) || 0;
              const freeShippingReached = shippingMethods.some(
                (m) => m.free_shipping_threshold != null && cartTotal >= parseFloat(m.free_shipping_threshold)
              );
              return (
                <select
                    value={shippingMethod}
                    onChange={(e) => setShippingMethod(e.target.value)}
                    required
                    className="input"
                    disabled={freeShippingReached}
                  >
                    <option value="">{t('chooseShipping')}</option>
                    {shippingMethods.map((method) => {
                      const cost = getShippingCost(method);
                      const isFreeByThreshold = method.free_shipping_threshold && cartTotal >= parseFloat(method.free_shipping_threshold);
                      const isFree = isFreeByThreshold || cost < 0.01;
                      const displayName = (language === 'en' && method.name_en) ? method.name_en
                        : (language === 'de' && method.name_de) ? method.name_de
                        : (method.name_lt || method.name);
                      const displayDesc = (language === 'en' && method.description_en) ? method.description_en
                        : (language === 'de' && method.description_de) ? method.description_de
                        : (method.description_lt || method.description);
                      return (
                        <option key={method.id} value={method.id}>
                          {displayName} - {isFree ? t('freeShipping') : `${cost.toFixed(2)} €`}
                          {displayDesc ? ` (${displayDesc})` : ''}
                        </option>
                      );
                    })}
                  </select>
              );
            })()
          ) : (
            <p className="no-shipping-methods">{t('noShippingMethods')}</p>
          )}
        </div>

        <div className="form-section">
          <h2>{t('paymentMethod')}</h2>
          {(() => {
            if (!paymentMethods || !Array.isArray(paymentMethods)) {
              return <p className="no-payment-methods">{t('loading')}</p>;
            }

            // Filtruoti tik aktyvius mokėjimo būdus
            const activePaymentMethods = paymentMethods.filter(m => {
              // Patikrinti visus galimus laukus, kurie gali nurodyti, ar mokėjimo būdas aktyvus
              const isEnabled = m.is_enabled === true || 
                               m.is_enabled === 1 || 
                               m.is_enabled === '1' ||
                               String(m.is_enabled).toLowerCase() === 'true';
              
              const isActive = m.active === true ||
                              m.active === 1 ||
                              m.active === '1' ||
                              String(m.active).toLowerCase() === 'true';
              
              const statusActive = m.status === 'active' ||
                                  m.status === 'enabled' ||
                                  m.status === 1;
              
              // Jei nėra jokio lauko, kuris nurodo, kad neaktyvus, laikyti aktyviu
              const notDisabled = m.is_enabled !== false &&
                                 m.is_enabled !== 0 &&
                                 m.is_enabled !== '0' &&
                                 m.active !== false &&
                                 m.active !== 0 &&
                                 m.active !== '0' &&
                                 m.status !== 'disabled' &&
                                 m.status !== 'inactive';
              
              return isEnabled || isActive || statusActive || (notDisabled && m.is_enabled !== null && m.active !== null);
            });
            
            const hasStripe = activePaymentMethods.some(m => (m.method_key || '').toLowerCase() === 'stripe');
            return activePaymentMethods.length > 0 ? (
              <div className="payment-methods-list">
                {activePaymentMethods.map((method) => {
                  const displayName = (language === 'en' && method.name_en) ? method.name_en
                    : (language === 'de' && method.name_de) ? method.name_de
                    : (method.name_lt || method.method_name);
                  const mode = method.display_mode || 'name_and_image';
                  const showName = mode === 'name' || mode === 'name_and_image' || (mode === 'image' && !method.image_url);
                  const showImage = (mode === 'image' || mode === 'name_and_image') && method.image_url;
                  return (
                    <label key={method.id} className="payment-method-option">
                      <input
                        type="radio"
                        name="payment"
                        value={method.method_key}
                        checked={paymentMethod === method.method_key}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        required
                      />
                      {showImage && (
                        <span className="payment-method-image">
                          <img src={getPaymentImageSrc(method.image_url)} alt="" />
                        </span>
                      )}
                      {showName && <span className="payment-method-name">{displayName}</span>}
                    </label>
                  );
                })}
                {hasStripe && (() => {
                  const stripeMethod = activePaymentMethods.find(m => (m.method_key || '').toLowerCase() === 'stripe');
                  const cardName = language === 'en' ? (stripeMethod?.stripe_card_name_en || t('paymentOptionCard')) : language === 'de' ? (stripeMethod?.stripe_card_name_de || t('paymentOptionCard')) : (stripeMethod?.stripe_card_name_lt || t('paymentOptionCard'));
                  const applePayName = language === 'en' ? (stripeMethod?.stripe_apple_pay_name_en || t('paymentOptionApplePay')) : language === 'de' ? (stripeMethod?.stripe_apple_pay_name_de || t('paymentOptionApplePay')) : (stripeMethod?.stripe_apple_pay_name_lt || t('paymentOptionApplePay'));
                  const googlePayName = language === 'en' ? (stripeMethod?.stripe_google_pay_name_en || t('paymentOptionGooglePay')) : language === 'de' ? (stripeMethod?.stripe_google_pay_name_de || t('paymentOptionGooglePay')) : (stripeMethod?.stripe_google_pay_name_lt || t('paymentOptionGooglePay'));
                  return (
                    <>
                      <label className="payment-method-option payment-method-option-card">
                        <input
                          type="radio"
                          name="payment"
                          value="stripe_card"
                          checked={paymentMethod === 'stripe_card'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                        {stripeMethod?.stripe_card_image_url ? (
                          <span className="payment-method-image">
                            <img src={getPaymentImageSrc(stripeMethod.stripe_card_image_url)} alt="" />
                          </span>
                        ) : (
                          <span className="payment-method-icon payment-method-icon-card" aria-hidden="true">
                            <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="0.5" y="2" width="31" height="20" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                              <path d="M0.5 8h31" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                          </span>
                        )}
                        <span className="payment-method-name">{cardName}</span>
                      </label>
                      <label className="payment-method-option payment-method-option-apple">
                        <input type="radio" name="payment" value="stripe_apple_pay" checked={paymentMethod === 'stripe_apple_pay'} onChange={(e) => setPaymentMethod(e.target.value)} />
                        {stripeMethod?.stripe_apple_pay_image_url ? (
                          <span className="payment-method-image">
                            <img src={getPaymentImageSrc(stripeMethod.stripe_apple_pay_image_url)} alt="" />
                          </span>
                        ) : (
                          <span className="payment-method-icon payment-method-icon-apple" aria-hidden="true" title={applePayName}>
                            <span className="apple-pay-badge">{applePayName}</span>
                          </span>
                        )}
                        {stripeMethod?.stripe_apple_pay_image_url && <span className="payment-method-name">{applePayName}</span>}
                      </label>
                      <label className="payment-method-option payment-method-option-google">
                        <input type="radio" name="payment" value="stripe_google_pay" checked={paymentMethod === 'stripe_google_pay'} onChange={(e) => setPaymentMethod(e.target.value)} />
                        {stripeMethod?.stripe_google_pay_image_url ? (
                          <span className="payment-method-image">
                            <img src={getPaymentImageSrc(stripeMethod.stripe_google_pay_image_url)} alt="" />
                          </span>
                        ) : (
                          <span className="payment-method-icon payment-method-icon-google" aria-hidden="true" title={googlePayName}>
                            <span className="google-pay-badge">{googlePayName}</span>
                          </span>
                        )}
                        {stripeMethod?.stripe_google_pay_image_url && <span className="payment-method-name">{googlePayName}</span>}
                      </label>
                    </>
                  );
                })()}
                {hasStripe && paymentMethod === 'stripe' && (() => {
                  const stripeMethod = activePaymentMethods.find(m => (m.method_key || '').toLowerCase() === 'stripe');
                  const stripeTypes = Array.isArray(stripeMethod?.stripe_payment_types) ? stripeMethod.stripe_payment_types : [];
                  const typeToLabel = {
                    card: 'stripeOptionCard',
                    paypal: 'stripeOptionPayPal',
                    link: 'stripeOptionLink',
                    ideal: 'stripeOptionIdeal',
                    giropay: 'stripeOptionGiropay',
                    bancontact: 'stripeOptionBancontact',
                    sofort: 'Sofort',
                    eps: 'EPS',
                    p24: 'Przelewy24',
                    alipay: 'Alipay',
                    klarna: 'Klarna',
                    affirm: 'Affirm',
                  };
                  if (stripeTypes.length === 0) return null;
                  const options = stripeTypes.map(value => ({ value, labelKey: typeToLabel[value] || value }));
                  return (
                    <div className="stripe-payment-options">
                      <p className="stripe-options-label">
                        {language === 'en' ? 'Choose how to pay with Stripe:' : language === 'de' ? 'Wählen Sie die Zahlungsart:' : 'Pasirinkite mokėjimo būdą:'}
                      </p>
                      <div className="stripe-options-list">
                        {options.map((opt) => (
                          <label key={opt.value} className="stripe-option-item">
                            <input
                              type="radio"
                              name="stripe_payment_option"
                              value={opt.value}
                              checked={stripePaymentOption === opt.value}
                              onChange={() => setStripePaymentOption(opt.value)}
                            />
                            <span>{typeof opt.labelKey === 'string' && opt.labelKey.startsWith('stripeOption') ? t(opt.labelKey) : opt.labelKey}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <p className="no-payment-methods">{t('noPaymentMethods')}</p>
            );
          })()}
        </div>

        {token && availableCredits > 0 && (
          <div className="form-section">
            <h2>{t('storeCredits')}</h2>
            <div className="store-credits-section">
              <div className="credits-info">
                <p>{t('storeCreditsAvailable')}: <strong>{parseFloat(availableCredits).toFixed(2)} €</strong></p>
              </div>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={useStoreCredits}
                  onChange={(e) => {
                    setUseStoreCredits(e.target.checked);
                    if (e.target.checked) {
                      const cartTotal = parseFloat(cart?.total) || 0;
                      let shipping = 5.99;
                      if (shippingMethod && shippingMethods) {
                        const method = shippingMethods.find(m => m.id === parseInt(shippingMethod));
                        if (method) {
                          shipping = (method.free_shipping_threshold && cartTotal >= method.free_shipping_threshold)
                            ? 0
                            : (parseFloat(method.cost) || 5.99);
                        }
                      }
                      const orderTotal = cartTotal + shipping;
                      const maxToUse = Math.min(availableCredits, orderTotal);
                      setStoreCreditsToUse(maxToUse);
                    } else {
                      setStoreCreditsToUse(0);
                    }
                  }}
                />
                {t('useStoreCredits')}
              </label>
              {useStoreCredits && (
                <div className="form-group">
                  <label>{t('howMuchCredits').replace('{amount}', parseFloat(availableCredits).toFixed(2))}</label>
                  <input
                    type="number"
                    min="0"
                    max={availableCredits}
                    step="0.01"
                    value={storeCreditsToUse}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      const cartTotal = parseFloat(cart?.total) || 0;
                      let shipping = 5.99;
                      if (shippingMethod && shippingMethods) {
                        const method = shippingMethods.find(m => m.id === parseInt(shippingMethod));
                        if (method) {
                          shipping = (method.free_shipping_threshold && cartTotal >= method.free_shipping_threshold)
                            ? 0
                            : (getShippingCost(method) || 5.99);
                        }
                      }
                      const orderTotal = cartTotal + shipping;
                      const maxToUse = Math.min(availableCredits, orderTotal);
                      setStoreCreditsToUse(Math.min(value, maxToUse));
                    }}
                    className="input"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div className="order-summary">
          <h2>{t('orderSummary')}</h2>
          
          {/* Prekių sąrašas */}
          <div className="checkout-items">
            {cart.items.map((item) => (
              <div key={item.id} className="checkout-item">
                {(item.variant_image || item.image) && (
                  <img src={item.variant_image || item.image} alt={item.name} className="checkout-item-image" />
                )}
                <div className="checkout-item-info">
                  <h4>{item.name}</h4>
                  <p className="item-sku">SKU: {item.variant_sku || item.sku || 'N/A'}</p>
                </div>
                <div className="checkout-item-quantity">
                  <span className="item-quantity-label">{t('quantity')}:</span>
                  <span className="item-quantity-value">{item.quantity}</span>
                </div>
                <div className="checkout-item-price">
                  <div className="item-price-container">
                    <span className="item-price">{item.price ? parseFloat(item.price).toFixed(2) : '0.00'} €</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {(() => {
            const finalShippingCountry = sameAsBilling ? billingAddress.country : shippingAddress.country;
            const taxRate = getTaxRateForCountry(finalShippingCountry);
            // Visos prekių kainos (cart.total) – su PVM. PVM santraukoje išskaiciuojamas iš sumos (DE 19%, kiti 21%).
            const subtotalWithTax = parseFloat(cart.total) || 0;
            const cartNet = subtotalWithTax / (1 + taxRate);
            const cartTax = subtotalWithTax - cartNet;

            let shippingWithTax = 0;
            if (shippingMethod && shippingMethods) {
              const method = shippingMethods.find(m => m.id === parseInt(shippingMethod));
              if (method) {
                const cartTotalValue = parseFloat(cart.total) || 0;
                if (method.free_shipping_threshold && cartTotalValue >= method.free_shipping_threshold) {
                  shippingWithTax = 0;
                } else {
                  shippingWithTax = getShippingCost(method) || 0;
                }
              }
            } else {
              shippingWithTax = 5.99;
            }

            const shippingTax = shippingWithTax - shippingWithTax / (1 + taxRate);
            const totalTax = cartTax + shippingTax;
            const totalWithTax = subtotalWithTax + shippingWithTax;

            const creditsUsed = useStoreCredits && storeCreditsToUse > 0 ? parseFloat(storeCreditsToUse) : 0;
            const finalTotal = totalWithTax - creditsUsed;

            return (
              <>
                <div className="summary-row">
                  <span>{t('subtotal')}:</span>
                  <span>{subtotalWithTax.toFixed(2)} €</span>
                </div>
                <div className="summary-row summary-shipping">
                  <span>{t('shippingCost')}:</span>
                  <span>
                    {shippingWithTax === 0 ? t('freeShipping') : `${shippingWithTax.toFixed(2)} €`}
                  </span>
                </div>
                <div className="summary-row summary-tax">
                  <span>{t('taxAmount')}:</span>
                  <span>{totalTax.toFixed(2)} €</span>
                </div>
                {useStoreCredits && storeCreditsToUse > 0 && (
                  <div className="summary-row discount">
                    <span>{t('storeCredits')}:</span>
                    <span>-{parseFloat(storeCreditsToUse).toFixed(2)} €</span>
                  </div>
                )}
                <div className="summary-row total">
                  <span>{t('total')}:</span>
                  <span>{finalTotal.toFixed(2)} €</span>
                </div>
              </>
            );
          })()}
        </div>

        <div className="form-section checkout-terms-agree">
          <label className="checkout-terms-label">
            <input
              type="checkbox"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              className="input-checkbox"
            />
            <span>
              Pateikdamas šį užsakymą sutinku su{' '}
              <Link to="/page/terms-of-use" target="_blank" rel="noopener noreferrer">naudojimosi sąlygomis</Link>,{' '}
              <Link to="/page/delivery" target="_blank" rel="noopener noreferrer">prekių pristatymo</Link> ir{' '}
              <Link to="/page/return" target="_blank" rel="noopener noreferrer">grąžinimo sąlygomis</Link>.
            </span>
          </label>
        </div>

        <button
          type="submit"
          disabled={createOrderMutation.isLoading || createStripeCheckoutMutation.isLoading}
          className="btn btn-primary btn-large"
        >
          {(createOrderMutation.isLoading || createStripeCheckoutMutation.isLoading) ? t('creatingOrder') : t('placeOrder')}
        </button>
      </form>
      </div>
    </div>
  );
}

export default Checkout;
